import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, { ReactNode, FunctionComponent } from 'react'

import { overrideOptional } from '@emico/overrides'

import FieldError from './FieldError'
import useNonSpammyFieldState from './useNonSpammyFieldState'

interface FormFieldProps {
  /** An optional label, this should generally be provided with exceptions like a StyledRadio */
  label?: ReactNode

  /** Elements with an input */
  children: ReactNode

  /** When provided will make the form field show as successful */
  success?: boolean

  /** When provided will make the form field show as loading */
  isLoading?: boolean

  /** When provided will make the form field show as successful with additional checkmark */
  showIsValid?: boolean

  /** A name that is used for aria labels. Make sure this is the same as the input's ID */
  field: string

  /** If true, display will be inline-block instead of block */
  inline?: boolean

  flyUpLabel?: boolean

  value?: string

  className?: string
}

interface FlyUpLabelProps {
  hasFocus: boolean
}

export const FlyUpLabel = overrideOptional(styled.span<FlyUpLabelProps>`
  position: absolute;
  pointer-events: none;
  margin: 3px;
  left: 3px;
  transform-origin: top left;
  transition: transform 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  *:focus + & {
    transform: scale(0.8) translateY(-1.4em) translateX(-9px);
  }

  ${({ hasFocus }) =>
    hasFocus &&
    css`
      transform: scale(0.8) translateY(-1.4em) translateX(-9px);
    `}
`)

const renderFlyUpLabel = (label: ReactNode, isUp: boolean) => (
  <FlyUpLabel hasFocus={isUp}>{label}</FlyUpLabel>
)

interface FormFieldLabelProps {
  withFlyUp?: boolean
}

export const FormFieldLabel = overrideOptional(styled.span<FormFieldLabelProps>`
  font-size: small;
  display: block;

  ${({ withFlyUp }) => withFlyUp && `color: transparent; pointer-events: none;`}
`)

interface FormFieldWrapperProps {
  error: boolean
  success: boolean
  inline: boolean
  isLoading?: boolean
}

export const FormFieldWrapper = overrideOptional(styled.div<FormFieldWrapperProps>`
  border: solid thin transparent;
  padding: 0.2em;
  margin: 0.2em -0.2em;
  border-radius: 0.2em;
  position: relative;

  ${({ inline }) => inline && `display: inline-block;`}
  ${({ error }) => error && `border-color: red;`}
  ${({ error, success }) => !error && success && `border-color: green;`}
`)

const FormFieldLabelWrapper = styled.label`
  display: block;
  margin: 1rem 0 0 0;
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormFieldControlFc: FunctionComponent<any> = ({ children }) => children
export const FormFieldControl = overrideOptional(FormFieldControlFc)

/**
 * The FormField component is used to decorate Text and Select components
 */
const FormField = ({
  label,
  children,
  success,
  isLoading,
  showIsValid,
  inline,
  field,
  flyUpLabel,
  ...props
}: FormFieldProps) => {
  const fieldState = useNonSpammyFieldState(field)

  const error = fieldState?.error
  const isValidOrSuccess = Boolean(
    showIsValid ? !error && fieldState?.value : !error && success,
  )

  // Radio's have their own labels per option. They shouldn't be wrapped in another label.
  const visualLabel = (
    <FormFieldLabel withFlyUp={Boolean(flyUpLabel)}>{label}</FormFieldLabel>
  )

  const isUp = fieldState?.value !== ''

  const flyUp = flyUpLabel ? renderFlyUpLabel(label || '', Boolean(isUp)) : null
  const control = (
    <FormFieldControl
      success={isValidOrSuccess}
      error={error}
      loading={Boolean(isLoading)}
      inline={Boolean(inline)}
    >
      {children}
    </FormFieldControl>
  )
  const childrenWithLabel = label ? (
    <FormFieldLabelWrapper>
      {visualLabel}
      {control}
      {flyUp}
    </FormFieldLabelWrapper>
  ) : (
    control
  )

  return (
    <FormFieldWrapper
      success={isValidOrSuccess}
      error={Boolean(error)}
      inline={Boolean(inline)}
      isLoading={Boolean(isLoading)}
      {...props}
    >
      {childrenWithLabel}
      <FieldError field={field} />
    </FormFieldWrapper>
  )
}

export default overrideOptional(FormField)
